var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ButtonType } from "flavr-common";
import { ShowModelIndicatorMessage } from "@messages/indicator";
import { MessageHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { ModelIndicator } from "./ModelIndicator";
/**
 * Shows lightweight icon indicators above models so interaction hints can be attached without modifying the underlying model content.
 *
 * Messages:
 * - `ShowModelIndicatorMessage` - Adds or removes indicator icons for a model.
 *
 * Events:
 * - `ModelEventType.TRANSFORM_UPDATED` - Repositions the indicator's world position so it stays anchored above the model after it moves.
 */
export class ModelIndicatorModule extends NewModule {
    constructor() {
        super("ModelIconModule");
        this.modelIndicatorMap = new Map();
    }
    /**
     * Creates or updates the indicator for the addressed model and applies the icon change described by the incoming message.
     */
    handleShowIconMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            let indicator = this.modelIndicatorMap.get(message.uid);
            if (indicator === undefined) {
                if (!message.show)
                    return;
                indicator = new ModelIndicator();
                this.modelIndicatorMap.set(message.uid, indicator);
            }
            if (message.show) {
                yield indicator.addIcon({ url: message.url, color: this.buttonTypeToColor(message.button) });
            }
            else {
                indicator.removeIcon(message.url);
            }
            indicator.update();
        });
    }
    /**
     * Repositions the indicator after the given model has moved so the icon stays anchored above it.
     */
    handleModelTransformEvent(model, _localTransform) {
        const indicator = this.modelIndicatorMap.get(model.getUID());
        if (indicator === undefined)
            return;
        indicator.setWorldPosition(model.getWorldPosition());
        indicator.update();
    }
    handleModelDestroyedEvent(model) {
        const uid = model.getUID();
        const indicator = this.modelIndicatorMap.get(uid);
        if (indicator === undefined)
            return;
        // The model this indicator was tracking (e.g. a controller that got torn down during an
        // input-mode switch) is gone and will no longer send transform updates, so its sprites
        // would otherwise be stuck floating at their last known position forever.
        indicator.destroy();
        this.modelIndicatorMap.delete(uid);
    }
    /// Private helper methods ///
    buttonTypeToColor(button) {
        switch (button) {
            case ButtonType.ALL:
            case ButtonType.CLICK:
                return 0xffffff;
            case ButtonType.SELECT:
                return 0xff0000;
            case ButtonType.DISCARD:
                return 0x0000ff;
            case ButtonType.BUTTON_A:
                return 0x00ff00;
            case ButtonType.GRAB:
                return 0xff00ff;
        }
    }
}
__decorate([
    MessageHandler(ShowModelIndicatorMessage)
], ModelIndicatorModule.prototype, "handleShowIconMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], ModelIndicatorModule.prototype, "handleModelTransformEvent", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], ModelIndicatorModule.prototype, "handleModelDestroyedEvent", null);
